import React, { useState, useMemo } from "react"
import {
  useGetSimulationRunLogsQuery,
  RunLogEntry,
} from "../../../app/services/meseeknaApiV2"

type RunLogViewerProps = {
  runId: number
  onClose: () => void
}

// Color mapping for different log entry types
const typeColors: Record<string, { bg: string; text: string; border: string }> = {
  decision: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  message: { bg: "bg-green-50", text: "text-green-700", border: "border-green-200" },
  plan: { bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200" },
  cplan: { bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200" },
  eplan: { bg: "bg-indigo-50", text: "text-indigo-700", border: "border-indigo-200" },
  search: { bg: "bg-yellow-50", text: "text-yellow-700", border: "border-yellow-200" },
  alert: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200" },
  info: { bg: "bg-gray-50", text: "text-gray-700", border: "border-gray-200" },
  default: { bg: "bg-gray-50", text: "text-gray-600", border: "border-gray-200" },
}

const getTypeColors = (type: string) => {
  return typeColors[type.toLowerCase()] || typeColors.default
}

const formatElapsedTime = (elapsedTime: string) => {
  // elapsedTime comes as HH:MM:SS or similar time format
  if (!elapsedTime) return "--:--:--"

  // If it's a full datetime string, extract just the time portion
  if (elapsedTime.includes("T")) {
    const timePart = elapsedTime.split("T")[1]
    return timePart?.split(".")[0] || elapsedTime
  }

  return elapsedTime
}

const formatDateTime = (dateTime: string) => {
  if (!dateTime) return "N/A"
  try {
    return new Date(dateTime).toLocaleString()
  } catch {
    return dateTime
  }
}

const RunLogViewer: React.FC<RunLogViewerProps> = ({ runId, onClose }) => {
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedTypes, setSelectedTypes] = useState<string[]>([])
  const [expandedEntries, setExpandedEntries] = useState<Set<number>>(new Set())

  const { data, isLoading, error } = useGetSimulationRunLogsQuery(runId)

  const runLogs = data?.data

  // Get unique entry types for filter
  const entryTypes = useMemo(() => {
    if (!runLogs?.entries) return []
    const types = new Set(runLogs.entries.map((e) => e.type))
    return Array.from(types).sort()
  }, [runLogs?.entries])

  // Filter entries based on search and type filters
  const filteredEntries = useMemo(() => {
    if (!runLogs?.entries) return []

    return runLogs.entries.filter((entry) => {
      // Type filter
      if (selectedTypes.length > 0 && !selectedTypes.includes(entry.type)) {
        return false
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        return (
          entry.msgTitle?.toLowerCase().includes(query) ||
          entry.msgDescription?.toLowerCase().includes(query) ||
          entry.responseText?.toLowerCase().includes(query) ||
          entry.type.toLowerCase().includes(query)
        )
      }

      return true
    })
  }, [runLogs?.entries, selectedTypes, searchQuery])

  const toggleEntryExpand = (logId: number) => {
    setExpandedEntries((prev) => {
      const next = new Set(prev)
      if (next.has(logId)) {
        next.delete(logId)
      } else {
        next.add(logId)
      }
      return next
    })
  }

  const toggleTypeFilter = (type: string) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    )
  }

  const downloadJSON = () => {
    if (!runLogs) return
    const blob = new Blob([JSON.stringify(runLogs, null, 2)], {
      type: "application/json",
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `run-logs-${runId}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const downloadCSV = () => {
    if (!runLogs?.entries) return

    const headers = [
      "Log ID",
      "Type",
      "Elapsed Time",
      "Date/Time",
      "Title",
      "Description",
      "Response",
      "Category ID",
      "Plan ID",
      "Event ID",
      "B4",
      "B5",
      "B6",
      "B7",
    ]

    const rows = runLogs.entries.map((entry) => [
      entry.logId,
      entry.type,
      formatElapsedTime(entry.elapsedTime),
      formatDateTime(entry.msgDateTime),
      entry.msgTitle || "",
      entry.msgDescription || "",
      entry.responseText || "",
      entry.categoryId || "",
      entry.planId || "",
      entry.eventId || "",
      entry.B4 || "",
      entry.B5 || "",
      entry.B6 || "",
      entry.B7 || "",
    ])

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row
          .map((cell) =>
            typeof cell === "string" ? `"${cell.replace(/"/g, '""')}"` : cell
          )
          .join(",")
      ),
    ].join("\n")

    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `run-logs-${runId}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="flex items-center gap-3">
            <span className="loading loading-spinner loading-lg"></span>
            <span>Loading run logs...</span>
          </div>
        </div>
      </div>
    )
  }

  if (error || !runLogs) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md">
          <h3 className="text-lg font-semibold text-red-600 mb-2">
            Error Loading Run Logs
          </h3>
          <p className="text-gray-600 mb-4">
            {error ? "Failed to load run logs. Please try again." : "No run logs found for this simulation run."}
          </p>
          <button className="btn btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-[90vw] h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-start">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              Run Logs: {runLogs.simulation.name}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              User: {runLogs.user.name} ({runLogs.user.email}) | Run ID: {runId} |{" "}
              {runLogs.totalEntries} entries
            </p>
          </div>
          <button
            className="btn btn-ghost btn-sm btn-circle"
            onClick={onClose}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Controls */}
        <div className="px-6 py-3 border-b border-gray-200 flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px] max-w-md">
            <input
              type="text"
              placeholder="Search logs..."
              className="input input-bordered input-sm w-full"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Type Filters */}
          <div className="flex flex-wrap gap-2">
            {entryTypes.map((type) => {
              const colors = getTypeColors(type)
              const isSelected = selectedTypes.includes(type)
              return (
                <button
                  key={type}
                  className={`badge badge-lg cursor-pointer transition-all ${
                    isSelected
                      ? `${colors.bg} ${colors.text} ${colors.border} border-2`
                      : "badge-outline"
                  }`}
                  onClick={() => toggleTypeFilter(type)}
                >
                  {type} ({runLogs.entriesByType[type] || 0})
                </button>
              )
            })}
            {selectedTypes.length > 0 && (
              <button
                className="badge badge-lg badge-ghost cursor-pointer"
                onClick={() => setSelectedTypes([])}
              >
                Clear filters
              </button>
            )}
          </div>

          {/* Download Buttons */}
          <div className="flex gap-2 ml-auto">
            <button className="btn btn-sm btn-outline" onClick={downloadJSON}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              JSON
            </button>
            <button className="btn btn-sm btn-outline" onClick={downloadCSV}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              CSV
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="px-6 py-2 bg-gray-50 border-b border-gray-200">
          <div className="flex flex-wrap gap-4 text-sm">
            <span className="text-gray-600">
              Showing {filteredEntries.length} of {runLogs.totalEntries} entries
            </span>
            <span className="text-gray-400">|</span>
            <span className="text-gray-600">
              Entry types:{" "}
              {Object.entries(runLogs.entriesByType)
                .map(([type, count]) => `${type}: ${count}`)
                .join(", ")}
            </span>
          </div>
        </div>

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto p-6">
          {filteredEntries.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No entries match your search criteria
            </div>
          ) : (
            <div className="space-y-3">
              {filteredEntries.map((entry) => (
                <LogEntryCard
                  key={`${entry.runId}-${entry.logId}`}
                  entry={entry}
                  isExpanded={expandedEntries.has(entry.logId)}
                  onToggle={() => toggleEntryExpand(entry.logId)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

type LogEntryCardProps = {
  entry: RunLogEntry
  isExpanded: boolean
  onToggle: () => void
}

const LogEntryCard: React.FC<LogEntryCardProps> = ({
  entry,
  isExpanded,
  onToggle,
}) => {
  const colors = getTypeColors(entry.type)

  return (
    <div
      className={`rounded-lg border ${colors.border} ${colors.bg} transition-all`}
    >
      {/* Entry Header - Always visible */}
      <button
        className="w-full px-4 py-3 flex items-start gap-4 text-left"
        onClick={onToggle}
      >
        {/* Elapsed Time */}
        <div className="font-mono text-sm text-gray-500 whitespace-nowrap">
          [{formatElapsedTime(entry.elapsedTime)}]
        </div>

        {/* Type Badge */}
        <span
          className={`badge ${colors.text} ${colors.bg} border ${colors.border} whitespace-nowrap`}
        >
          {entry.type.toUpperCase()}
        </span>

        {/* Title and Description */}
        <div className="flex-1 min-w-0">
          <div className={`font-medium ${colors.text}`}>
            {entry.msgTitle || "Untitled Entry"}
          </div>
          {entry.msgDescription && !isExpanded && (
            <div className="text-sm text-gray-600 truncate mt-1">
              {entry.msgDescription}
            </div>
          )}
        </div>

        {/* Expand Icon */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-5 w-5 text-gray-400 transition-transform ${
            isExpanded ? "rotate-180" : ""
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-200 mt-2 pt-3 space-y-3">
          {entry.msgDescription && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase mb-1">
                Description
              </div>
              <div className="text-sm text-gray-700 whitespace-pre-wrap">
                {entry.msgDescription}
              </div>
            </div>
          )}

          {entry.responseText && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase mb-1">
                Response
              </div>
              <div className="text-sm text-gray-700 whitespace-pre-wrap bg-white rounded p-2 border border-gray-200">
                {entry.responseText}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase">
                Log ID
              </div>
              <div className="text-gray-700">{entry.logId}</div>
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase">
                Date/Time
              </div>
              <div className="text-gray-700">
                {formatDateTime(entry.msgDateTime)}
              </div>
            </div>
            {entry.categoryId && (
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase">
                  Category ID
                </div>
                <div className="text-gray-700">{entry.categoryId}</div>
              </div>
            )}
            {entry.planId && (
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase">
                  Plan ID
                </div>
                <div className="text-gray-700">{entry.planId}</div>
              </div>
            )}
            {entry.eventId && (
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase">
                  Event ID
                </div>
                <div className="text-gray-700">{entry.eventId}</div>
              </div>
            )}
          </div>

          {/* Measure Codes */}
          {(entry.B4 || entry.B5 || entry.B6 || entry.B7) && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase mb-1">
                Measure Codes
              </div>
              <div className="flex flex-wrap gap-2">
                {entry.B4 && (
                  <span className="badge badge-sm badge-outline">B4: {entry.B4}</span>
                )}
                {entry.B5 && (
                  <span className="badge badge-sm badge-outline">B5: {entry.B5}</span>
                )}
                {entry.B6 && (
                  <span className="badge badge-sm badge-outline">B6: {entry.B6}</span>
                )}
                {entry.B7 && (
                  <span className="badge badge-sm badge-outline">B7: {entry.B7}</span>
                )}
              </div>
            </div>
          )}

          {/* Category Level IDs */}
          {(entry.categoryLevel1Id ||
            entry.categoryLevel2Id ||
            entry.categoryLevel3Id ||
            entry.categoryLevel4Id ||
            entry.categoryLevel5Id) && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase mb-1">
                Category Hierarchy
              </div>
              <div className="text-sm text-gray-600">
                {[
                  entry.categoryLevel1Id,
                  entry.categoryLevel2Id,
                  entry.categoryLevel3Id,
                  entry.categoryLevel4Id,
                  entry.categoryLevel5Id,
                ]
                  .filter(Boolean)
                  .join(" > ")}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default RunLogViewer
